import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  MessageSquare, ClipboardList, Gamepad2, ArrowRight, Sparkles,
  Users, Store, TrendingUp, Activity, Car, Award, Ticket,
} from "lucide-react";

export default function AdminOverview() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const load = () => api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 15000); // refresh every 15s for live feel
    return () => clearInterval(t);
  }, []);

  const conversion = stats?.vouchers_total
    ? Math.round((stats.vouchers_redeemed / stats.vouchers_total) * 100) : 0;
  const engagement = (stats?.feedback_7d ?? 0) + (stats?.survey_responses_7d ?? 0);

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="phenix-pill bg-plum-100 text-plum-700 mb-2">
            <Sparkles size={11} strokeWidth={2.5} /> Mairie d&apos;Istres
          </div>
          <h1 className="heading-display text-2xl sm:text-3xl">
            Bonjour <span className="text-pink-500">{user.name.split(" ")[0]}</span>
          </h1>
          <p className="text-xs text-plum-700/65">Vue d&apos;ensemble de votre plateforme Istres &amp; Moi.</p>
        </div>
        <div className="phenix-card px-4 py-2.5 bg-gradient-to-br from-plum-600 to-plum-900 text-white border-0 flex items-center gap-3">
          <Activity size={20} className="text-sun-300" />
          <div>
            <div className="text-[9px] font-bold uppercase tracking-widest text-pink-200">Activité 7j</div>
            <div className="font-display font-extrabold text-2xl leading-none">{engagement}</div>
            <div className="text-[10px] text-plum-100">interactions citoyennes</div>
          </div>
        </div>
      </header>

      {/* Main KPIs */}
      <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5" data-testid="admin-stat-cards">
        <Stat to="/admin/utilisateurs" icon={Users} color="bg-pink-100 text-pink-600" label="Habitants" value={stats?.habitants ?? "–"} sub={stats?.users_new_30d != null ? `+${stats.users_new_30d} 30j` : ""} />
        <Stat to="/admin/utilisateurs" icon={Store} color="bg-sun-100 text-sun-700" label="Commerçants" value={stats?.commercants ?? "–"} />
        <Stat to="/admin/remontees" icon={MessageSquare} color="bg-pink-100 text-pink-600" label="Idées reçues" value={stats?.feedback_count ?? "–"} sub={stats ? `${stats.feedback_open} en attente` : ""} urgent={stats?.feedback_open > 0} />
        <Stat to="/admin/sondages" icon={ClipboardList} color="bg-plum-100 text-plum-600" label="Réponses sondages" value={stats?.survey_responses_total ?? "–"} sub={stats ? `+${stats.survey_responses_7d} cette sem.` : ""} />
        <Stat to="/admin/jeux" icon={Gamepad2} color="bg-sun-100 text-sun-700" label="Quiz joués" value={stats?.quiz_attempts_total ?? "–"} />
        <Stat icon={Ticket} color="bg-mint-100 text-mint-600" label="Bons" value={stats?.vouchers_total ?? "–"} sub={stats ? `${stats.vouchers_active} actifs` : ""} />
      </section>

      {/* Engagement panel */}
      <section className="grid lg:grid-cols-3 gap-3">
        <div className="phenix-card p-4 lg:col-span-2" data-testid="admin-activity-chart">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <TrendingUp size={16} className="text-plum-600" strokeWidth={2.4} />
              <h3 className="font-display font-bold text-[15px] text-plum-900">Activité des 7 derniers jours</h3>
            </div>
            <span className="text-[10px] text-plum-700/55 uppercase tracking-wider font-bold">idées + sondages + quiz</span>
          </div>
          <BarChart days={stats?.daily_activity || []} />
        </div>

        <div className="phenix-card p-4 bg-gradient-to-br from-sun-50 to-cream-50 border-sun-200" data-testid="admin-conversion">
          <div className="flex items-center gap-2 mb-2">
            <Award size={16} className="text-sun-600" strokeWidth={2.4} />
            <h3 className="font-display font-bold text-[15px] text-plum-900">Conversion bons</h3>
          </div>
          <div className="font-display font-extrabold text-4xl text-plum-900 leading-none">{conversion}%</div>
          <div className="text-[11px] text-plum-700/65 mt-1">
            {stats?.vouchers_redeemed ?? 0} bons utilisés sur {stats?.vouchers_total ?? 0}
          </div>
          <div className="mt-3 h-2 bg-cream-200 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-sun-400 to-pink-500 transition-all duration-700" style={{ width: `${conversion}%` }} />
          </div>
        </div>
      </section>

      {/* Parking analytics */}
      <section className="grid lg:grid-cols-3 gap-3" data-testid="admin-parking">
        <div className="phenix-card p-4 lg:col-span-2">
          <div className="flex items-center gap-2 mb-3">
            <Car size={16} className="text-plum-600" strokeWidth={2.4} />
            <h3 className="font-display font-bold text-[15px] text-plum-900">Parkings — itinéraires demandés</h3>
          </div>
          {stats?.parking_stats && stats.parking_stats.length > 0 ? (
            <div className="space-y-2">
              {stats.parking_stats.map((p, i) => {
                const max = Math.max(...stats.parking_stats.map((x) => x.clicks));
                const pct = max ? Math.round((p.clicks / max) * 100) : 0;
                return (
                  <div key={p.id} className="flex items-center gap-3">
                    <span className="font-mono text-[11px] text-plum-700/55 w-5">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between mb-1">
                        <span className="text-[13px] font-semibold text-plum-900 truncate">{p.name || p.id}</span>
                        <span className="text-[11px] font-mono font-bold text-plum-700 tabular-nums">{p.clicks}</span>
                      </div>
                      <div className="h-1.5 bg-cream-100 rounded-full overflow-hidden">
                        <div className="h-full bg-plum-500" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-[12px] text-plum-700/55 italic py-3">Aucun itinéraire vers un parking pour le moment.</p>
          )}
        </div>

        <div className="phenix-card p-4">
          <h3 className="font-display font-bold text-[15px] text-plum-900 mb-3">Affluence par jour (7j)</h3>
          {stats?.parking_by_weekday ? (
            <div className="grid grid-cols-7 gap-1 items-end h-32">
              {["lun", "mar", "mer", "jeu", "ven", "sam", "dim"].map((d) => {
                const val = stats.parking_by_weekday[d] || 0;
                const max = Math.max(...Object.values(stats.parking_by_weekday), 1);
                const h = (val / max) * 100;
                return (
                  <div key={d} className="flex flex-col items-center justify-end gap-1 h-full">
                    <span className="text-[10px] font-mono text-plum-700/70 tabular-nums">{val}</span>
                    <div className="w-full bg-cream-100 rounded-t flex-1 flex items-end">
                      <div className="w-full bg-gradient-to-t from-plum-700 to-pink-400 rounded-t transition-all" style={{ height: `${Math.max(h, 4)}%` }} />
                    </div>
                    <span className="text-[10px] uppercase tracking-wider text-plum-700/60 font-bold">{d}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-[12px] text-plum-700/55 italic">—</p>
          )}
        </div>
      </section>

      {/* Quick links */}
      <section className="grid sm:grid-cols-3 gap-2.5">
        <Tile to="/admin/remontees" icon={MessageSquare} title="Modérer les idées" desc="Lire, répondre, classifier" color="bg-pink-400" />
        <Tile to="/admin/sondages" icon={ClipboardList} title="Lancer un sondage" desc="Habitants, commerçants ou tous" color="bg-plum-600" />
        <Tile to="/admin/jeux" icon={Gamepad2} title="Créer un quiz" desc="Avec récompense locale" color="bg-sun-400" />
      </section>
    </div>
  );
}

function BarChart({ days }) {
  if (!days.length) return <p className="text-[12px] text-plum-700/55 italic">—</p>;
  const max = Math.max(...days.map((d) => d.total), 1);
  return (
    <div className="grid grid-cols-7 gap-1.5 items-end h-32">
      {days.map((d) => {
        const h = (d.total / max) * 100;
        return (
          <div key={d.date} className="flex flex-col items-center gap-1 h-full">
            <span className="text-[10px] font-mono tabular-nums text-plum-700/70">{d.total}</span>
            <div className="flex-1 w-full flex items-end">
              <div className="w-full bg-gradient-to-t from-plum-600 via-pink-500 to-sun-400 rounded-t transition-all" style={{ height: `${Math.max(h, 4)}%` }} title={`${d.date} · ${d.total}`} />
            </div>
            <span className="text-[10px] uppercase tracking-wider text-plum-700/60 font-bold">{d.weekday}</span>
          </div>
        );
      })}
    </div>
  );
}

function Stat({ to, icon: Icon, color, label, value, sub, urgent }) {
  const inner = (
    <div className="phenix-card p-3 h-full relative">
      {urgent && <div className="absolute top-2.5 right-2.5 w-2 h-2 rounded-full bg-pink-400 animate-pulse" />}
      <div className={`w-8 h-8 rounded-lg grid place-items-center ${color} mb-1.5`}>
        <Icon size={14} strokeWidth={2.4} />
      </div>
      <div className="font-display font-extrabold text-xl text-plum-900 leading-none">{value}</div>
      <div className="text-[10px] text-plum-700/60 mt-0.5">{label}</div>
      {sub && <div className="text-[10px] text-plum-700/50 italic mt-0.5">{sub}</div>}
    </div>
  );
  return to ? <Link to={to} className="block hover:-translate-y-0.5 transition-transform">{inner}</Link> : inner;
}

function Tile({ to, icon: Icon, title, desc, color }) {
  return (
    <Link to={to} className="phenix-card phenix-card-hover p-3 group flex items-center gap-3">
      <div className={`${color} text-white w-9 h-9 rounded-xl grid place-items-center shadow-sm shrink-0`}>
        <Icon size={15} strokeWidth={2.2} />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-display font-bold text-sm text-plum-900 leading-tight">{title}</h3>
        <p className="text-[11px] text-plum-700/65 line-clamp-1">{desc}</p>
      </div>
      <ArrowRight size={14} className="text-plum-400 group-hover:text-plum-600 group-hover:translate-x-0.5 transition-all" />
    </Link>
  );
}
